/**
 * OnConflictCard — renders the PG/SQLite `ON CONFLICT [target] DO UPDATE SET …`
 * (or `ON CONFLICT [target] DO NOTHING`) clause as an expandable card.
 *
 * Dual render path (§2.4 S6b):
 *   - with `editContext` AND a resolver-parsed `structure.onConflict`, the
 *     structure-driven `OnConflictCardEditable` mounts (action switch, target
 *     picker, editable assignments) — the renderer-side string scan below is
 *     NOT used;
 *   - without editContext (or when structure extraction degrades), the
 *     original read-only string-scan card renders unchanged — § 1.4
 *     expand/collapse UI state plus the disabled SetAssignmentsList scaffolding.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { Button } from '../../../../../components/ui/button';
import type { VqeEditContext } from '../../edit/editContext';
import { useVqeStore } from '../../store/vqeStore';
import { ClauseCard } from '../select/ClauseCard';
import { VqeTooltip } from '../common/VqeTooltip';
import { SetAssignmentsList } from '../update/SetAssignmentsList';
import { safeExtractStructure } from './insertColumnMapHelpers';
import { OnConflictCardEditable } from './OnConflictCardEditable';

export interface OnConflictCardProps {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  /**
   * Seed for the expand/collapse store key. MUST be stable across buffer
   * commits: statement ids are content-derived (sha1 of text+offset), so
   * keying on one would collapse the card after every edit committed from
   * inside its own expanded body. The renderer passes the workspace tab id
   * (one statement renders per tab, so it can't collide).
   */
  readonly expansionScope: string;
  /** Present only for plain-INSERT mounts — enables the editable path. */
  readonly editContext?: VqeEditContext;
}

export function OnConflictCard({
  bufferText,
  start,
  end,
  options,
  expansionScope,
  editContext,
}: OnConflictCardProps) {
  const structure = editContext ? safeExtractStructure(editContext) : undefined;
  const onConflict = structure?.onConflict;
  if (editContext && structure && onConflict) {
    return (
      <OnConflictCardEditable
        bufferText={bufferText}
        options={options}
        expansionScope={expansionScope}
        editContext={editContext}
        structure={structure}
        onConflict={onConflict}
      />
    );
  }
  return (
    <OnConflictCardReadOnly
      bufferText={bufferText}
      start={start}
      end={end}
      options={options}
      expansionScope={expansionScope}
    />
  );
}

function OnConflictCardReadOnly({
  bufferText,
  start,
  end,
  options,
  expansionScope,
}: Omit<OnConflictCardProps, 'editContext'>) {
  // Same key family as the editable card so expansion survives the
  // read-only → editable dispatch (editContext/schema arriving late).
  const expansionKey = `${expansionScope}:on-conflict`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);

  const span = bufferText.slice(start, end);
  const parsed = parseOnConflict(span);

  return (
    <ClauseCard
      title="On conflict"
      testId="vqe-insert-on-conflict"
      accessory={
        <span
          className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400"
          data-testid="vqe-insert-on-conflict-action"
        >
          {parsed.action}
        </span>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <VqeTooltip content={expanded ? 'Collapse' : 'Expand'}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setExpansion(expansionKey, !expanded)}
              aria-expanded={expanded}
              data-testid="vqe-insert-on-conflict-toggle"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </VqeTooltip>
          <span
            className="flex-1 truncate font-mono text-xs"
            data-testid="vqe-insert-on-conflict-summary"
          >
            {parsed.summary}
          </span>
        </div>
        {expanded ? (
          <div className="rounded-md border border-border bg-background p-2" data-testid="vqe-insert-on-conflict-body">
            {parsed.action === 'DO NOTHING' ? (
              <p className="text-xs italic text-muted-foreground" data-testid="vqe-insert-on-conflict-do-nothing">
                Conflict rows are ignored — no assignments.
              </p>
            ) : (
              // An unparsed span is empty: the list says "No assignments parsed."
              <SetAssignmentsList
                bufferText={bufferText}
                start={start + (parsed.assignmentsStart ?? 0)}
                end={start + (parsed.assignmentsEnd ?? parsed.assignmentsStart ?? 0)}
                options={options}
                testIdPrefix="vqe-insert-on-conflict"
              />
            )}
          </div>
        ) : null}
      </div>
    </ClauseCard>
  );
}

interface ParsedOnConflict {
  readonly action: 'DO UPDATE' | 'DO NOTHING' | 'UNKNOWN';
  readonly summary: string;
  readonly assignmentsStart: number | null;
  readonly assignmentsEnd: number | null;
}

function parseOnConflict(span: string): ParsedOnConflict {
  const upper = span.toUpperCase();
  const doIdx = upper.indexOf(' DO ');
  if (doIdx < 0) {
    return { action: 'UNKNOWN', summary: span.trim(), assignmentsStart: null, assignmentsEnd: null };
  }
  const after = upper.slice(doIdx + 4).trimStart();
  if (after.startsWith('NOTHING')) {
    return { action: 'DO NOTHING', summary: span.trim(), assignmentsStart: null, assignmentsEnd: null };
  }
  if (after.startsWith('UPDATE')) {
    const setIdx = upper.indexOf(' SET ', doIdx);
    if (setIdx >= 0) {
      const start = setIdx + 5;
      return {
        action: 'DO UPDATE',
        summary: span.slice(0, doIdx).trim(),
        assignmentsStart: start,
        assignmentsEnd: span.length,
      };
    }
    return { action: 'DO UPDATE', summary: span.trim(), assignmentsStart: null, assignmentsEnd: null };
  }
  return { action: 'UNKNOWN', summary: span.trim(), assignmentsStart: null, assignmentsEnd: null };
}
